// Whitelists de tri cote serveur pour les vues SQL paginees.
//
// PostgREST renvoie HTTP 400 si `.order()` cible une colonne inexistante.
// Plusieurs colonnes de tableau exposent des relations enrichies cote client
// (ex: `assigned_user` sur ContactsListPage, `assigne_a` sur ProspectsListPage)
// qui ne sont PAS des colonnes des vues — `contacts_filter_view` n'expose que
// `assigned_to`, `prospects_view` que `contact_assigned_to`. Un clic de tri sur
// ces en-tetes emettait `order=assigned_user` -> 400 -> liste cassee.
//
// resolveSortColumn rabat tout champ hors-whitelist sur un fallback
// (`created_at` par defaut). C'est la defense serveur derriere `enableSorting:
// false` pose sur les colonnes-relations cote table : meme si une future
// colonne oublie ce flag, aucun 400 ne peut remonter.
//
// A garder en phase avec les migrations qui definissent les vues :
//   - contacts_filter_view : 00032 / 00041 / 00095
//   - prospects_view       : migrations BPI
//   - crews_filter_view    : 00032 / 00039 / 00050 / 00086 / 00093 / 00095 /
//                            00096 / 00097 / 00172 (derniere recreation)

export const CONTACTS_FILTER_VIEW_SORT_COLUMNS = [
  'id',
  'first_name',
  'last_name',
  'email',
  'phone',
  'status',
  'origin_id',
  'assigned_to',
  'notes',
  'created_at',
  'updated_at',
  'school_id',
  'address',
  'city',
  'postal_code',
  'country',
  'is_not_student',
  'socio_professional_category',
  'birth_date',
  'product_ids',
  'product_names',
  'call_count',
  'last_call_at',
  'crew_numbers',
  'is_in_crew',
] as const;

export const PROSPECTS_VIEW_SORT_COLUMNS = [
  'id',
  'form_id',
  'contact_id',
  'data',
  'bpi_code',
  'created_at',
  'converted_at',
  'form_name',
  'form_slug',
  'origin_id',
  'origin_name',
  'origin_label',
  'product_id',
  'product_name',
  'contact_first_name',
  'contact_last_name',
  'contact_email',
  'contact_phone',
  'contact_assigned_to',
  'contact_last_call_at',
  'payment_status',
] as const;

// crews_filter_view : `SELECT cr.* + agregats` (derniere recreation : 00172).
// La liste n'est pas derivable du SQL du repo — le `cr.*` s'expanse a l'execution
// et Postgres fige les colonnes au CREATE. Relevee en prod le 2026-08-29 via :
//   select column_name from information_schema.columns
//   where table_name = 'crews_filter_view';
// Issue #230 : CrewsListPage expose 5 colonnes-relations enrichies cote client
// (members, product, vehicle, association, discount) absentes de la vue —
// `order=members` renvoyait un 400 (hint PostgREST : `member_ids`).
export const CREWS_FILTER_VIEW_SORT_COLUMNS = [
  'id',
  'name',
  'crew_number',
  'status',
  'probability',
  'product_id',
  'vehicle_id',
  'association_id',
  'discount_id',
  'assigned_to',
  'financing_type',
  'budget',
  'report',
  'has_derogation',
  'organizes_pvd',
  'notes',
  'created_at',
  'updated_at',
  'has_vehicle',
  'derogation_pending',
  'member_ids',
  'member_names',
  'call_count',
  'pilot_postal_code',
  'pilot_city',
  'pilot_full_name',
  'bpi_code',
] as const;

// Renvoie `field` s'il fait partie de la whitelist, sinon `fallback`.
export function resolveSortColumn(
  field: string | null | undefined,
  allowed: readonly string[],
  fallback = 'created_at'
): string {
  return field && allowed.includes(field) ? field : fallback;
}

// ProspectsListPage : les colonnes triables utilisent des `accessorFn` + un id
// UI francais (ex: 'nom', 'origine') qui ne correspond PAS au nom de colonne de
// prospects_view. Sans mapping, le tri serveur (`order=nom`) renverrait un 400.
// Cette table traduit chaque id UI triable vers sa colonne SQL reelle. Les ids
// deja identiques a une colonne (bpi_code, payment_status) n'ont pas d'alias ;
// 'assigne_a' n'est pas mappable (relation enrichie cote client, absente de la
// vue) -> il rabat sur le fallback via la whitelist (et la colonne est de toute
// facon enableSorting:false).
export const PROSPECTS_VIEW_SORT_ALIASES: Record<string, string> = {
  origine: 'origin_label',
  produit: 'product_name',
  nom: 'contact_last_name',
  prenom: 'contact_first_name',
  email: 'contact_email',
  telephone: 'contact_phone',
  dernier_appel: 'contact_last_call_at',
  date: 'created_at',
};

// Resout l'id de colonne UI d'un tri Prospects vers une colonne SQL sure :
// alias d'abord, puis whitelist (fallback created_at).
export function resolveProspectSortColumn(field: string | null | undefined): string {
  const aliased = field ? (PROSPECTS_VIEW_SORT_ALIASES[field] ?? field) : field;
  return resolveSortColumn(aliased, PROSPECTS_VIEW_SORT_COLUMNS);
}
